import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { CalendarDays, Loader2, MessageSquare, RefreshCw, Save, Search, Users } from 'lucide-react';
import { db } from '../../firebase/config';

const blankRecord = {
  meetingDate: '',
  mode: 'In Person',
  parentName: '',
  relation: '',
  contactNumber: '',
  pointsDiscussed: '',
  actionPlan: '',
  followUpDate: ''
};

const studentName = (student) => (
  `${student.firstName || ''} ${student.surname || ''}`.trim() ||
  student.name ||
  student.rollNumber
);

const recordId = (rollNumber, semester) => `${rollNumber}_semester${semester}`;

const ParentsMeetManagementWidget = ({ assignedBatch, userEmail, userName }) => {
  const [semester, setSemester] = useState('1');
  const [mentees, setMentees] = useState([]);
  const [records, setRecords] = useState({});
  const [selectedRoll, setSelectedRoll] = useState('');
  const [formData, setFormData] = useState(blankRecord);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [assignedBatch, userEmail]);

  useEffect(() => {
    const key = recordId(selectedRoll, semester);
    setFormData({
      ...blankRecord,
      ...(records[key] || {})
    });
  }, [records, selectedRoll, semester]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const batchPairs = [];

      if (userEmail) {
        const assignmentSnap = await getDocs(query(
          collection(db, 'batchAssignments'),
          where('mentorEmail', '==', userEmail)
        ));

        assignmentSnap.docs.forEach((assignmentDoc) => {
          const data = assignmentDoc.data();
          if (data.batch) {
            batchPairs.push({
              batch: data.batch,
              division: data.division || ''
            });
          }
        });
      }

      if (assignedBatch && !batchPairs.some((item) => item.batch === assignedBatch)) {
        batchPairs.push({ batch: assignedBatch, division: '' });
      }

      const [studentsSnap, recordsSnap] = await Promise.all([
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'parentMeetRecords'))
      ]);

      const assignedStudents = studentsSnap.docs
        .map((studentDoc) => ({ id: studentDoc.id, ...studentDoc.data() }))
        .filter((student) => batchPairs.some((assignment) => (
          student.batch === assignment.batch &&
          (!assignment.division || student.division === assignment.division)
        )))
        .sort((a, b) => (a.rollNumber || '').localeCompare(b.rollNumber || '', undefined, { numeric: true }));

      const recordMap = {};
      recordsSnap.docs.forEach((recordDoc) => {
        recordMap[recordDoc.id] = { id: recordDoc.id, ...recordDoc.data() };
      });

      setMentees(assignedStudents);
      setRecords(recordMap);
      setSelectedRoll((current) => (
        assignedStudents.some((student) => student.rollNumber === current)
          ? current
          : assignedStudents[0]?.rollNumber || ''
      ));
    } catch (loadError) {
      console.error('Error loading parent meet records:', loadError);
      setError('Could not load mentees or parent meet records.');
    } finally {
      setLoading(false);
    }
  };

  const selectedStudent = mentees.find((student) => student.rollNumber === selectedRoll);

  const filteredMentees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return mentees;

    return mentees.filter((student) => (
      (student.rollNumber || '').toLowerCase().includes(term) ||
      studentName(student).toLowerCase().includes(term) ||
      (student.division || '').toLowerCase().includes(term)
    ));
  }, [mentees, search]);

  const updateForm = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: field === 'contactNumber' ? value.replace(/\D/g, '').slice(0, 10) : value
    }));
  };

  const saveRecord = async () => {
    if (!selectedStudent) return;
    if (!formData.meetingDate || !formData.pointsDiscussed.trim()) {
      setError('Meeting date and points discussed are required.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const payload = {
        ...formData,
        rollNumber: selectedStudent.rollNumber,
        studentName: studentName(selectedStudent),
        semester: Number(semester),
        batch: selectedStudent.batch || '',
        division: selectedStudent.division || '',
        mentorName: userName || '',
        mentorEmail: userEmail || '',
        updatedAt: serverTimestamp()
      };
      const id = recordId(selectedStudent.rollNumber, semester);

      await setDoc(doc(db, 'parentMeetRecords', id), payload, { merge: true });

      setRecords((current) => ({
        ...current,
        [id]: { ...current[id], ...payload, id }
      }));
      setMessage(`Parent meet record saved for ${selectedStudent.rollNumber}.`);
    } catch (saveError) {
      console.error('Error saving parent meet record:', saveError);
      setError('Could not save parent meet record.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading parent meet records
      </div>
    );
  }

  if (mentees.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-amber-800">
        No mentees are assigned to this mentor yet.
      </div>
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(260px,360px)_1fr]">
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="space-y-3 border-b border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-2">
            <label>
              <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Semester</span>
              <select
                value={semester}
                onChange={(event) => {
                  setSemester(event.target.value);
                  setMessage('');
                  setError('');
                }}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="1">Semester 1</option>
                <option value="2">Semester 2</option>
              </select>
            </label>
            <div>
              <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Batch</span>
              <div className="flex items-center rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
                <Users className="mr-2 h-4 w-4 text-gray-500" />
                {assignedBatch || 'Assigned'}
              </div>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search mentees"
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <button
            type="button"
            onClick={loadData}
            className="inline-flex w-full items-center justify-center rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload
          </button>
        </div>

        <div className="max-h-[560px] overflow-y-auto">
          {filteredMentees.map((student) => {
            const hasRecord = Boolean(records[recordId(student.rollNumber, semester)]);

            return (
              <button
                key={student.rollNumber}
                type="button"
                onClick={() => {
                  setSelectedRoll(student.rollNumber);
                  setMessage('');
                  setError('');
                }}
                className={`block w-full border-b border-gray-100 px-4 py-3 text-left transition-colors ${
                  selectedRoll === student.rollNumber ? 'bg-emerald-50' : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="font-semibold text-gray-900">{student.rollNumber}</div>
                <div className="mt-1 text-xs text-gray-500">{studentName(student)}</div>
                <div className="mt-2 flex gap-2 text-[11px]">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">Div {student.division || '-'}</span>
                  <span className={`rounded-full px-2 py-0.5 ${hasRecord ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {hasRecord ? 'Recorded' : 'Pending'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {selectedStudent ? `${selectedStudent.rollNumber} - ${studentName(selectedStudent)}` : 'Parent Meet Record'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">Semester {semester} parent interaction</p>
            </div>
            <MessageSquare className="h-7 w-7 text-emerald-600" />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Meeting Date</span>
            <div className="relative">
              <CalendarDays className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={formData.meetingDate || ''}
                onChange={(event) => updateForm('meetingDate', event.target.value)}
                className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </label>

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Mode</span>
            <select
              value={formData.mode || 'In Person'}
              onChange={(event) => updateForm('mode', event.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              <option>In Person</option>
              <option>Phone Call</option>
              <option>Video Call</option>
              <option>WhatsApp</option>
            </select>
          </label>

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Parent/Guardian Name</span>
            <input
              value={formData.parentName || ''}
              onChange={(event) => updateForm('parentName', event.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Relation</span>
            <input
              value={formData.relation || ''}
              onChange={(event) => updateForm('relation', event.target.value)}
              placeholder="Father, Mother, Guardian"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </label>

          <label className="md:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Contact Number</span>
            <input
              value={formData.contactNumber || ''}
              onChange={(event) => updateForm('contactNumber', event.target.value)}
              maxLength="10"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </label>

          <label className="md:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Points Discussed</span>
            <textarea
              value={formData.pointsDiscussed || ''}
              onChange={(event) => updateForm('pointsDiscussed', event.target.value)}
              rows="5"
              placeholder="Attendance, academic progress, discipline, achievements, health, counselling, parent feedback..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </label>

          <label className="md:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Action Plan / Follow-up</span>
            <textarea
              value={formData.actionPlan || ''}
              onChange={(event) => updateForm('actionPlan', event.target.value)}
              rows="3"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Follow-up Date</span>
            <input
              type="date"
              value={formData.followUpDate || ''}
              onChange={(event) => updateForm('followUpDate', event.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </label>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={saveRecord}
            disabled={saving || !selectedStudent}
            className="inline-flex items-center justify-center rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:bg-gray-400"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Parent Meet
          </button>
          <p className="text-xs text-gray-500">This record appears in the student's End of Semester Report.</p>
        </div>

        {error && <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {message && <div className="mt-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{message}</div>}
      </div>
    </div>
  );
};

export default ParentsMeetManagementWidget;
